import React from 'react';
import toast from 'react-hot-toast';
import {
  Plan,
  resolveSubscriptionPlanPrice,
  calculateCombinedBundleSubtotal,
  calculateCombinedBundleTotal,
  COMBINED_BUNDLE_DISCOUNT_PERCENT,
} from '@court-sync/shared';
import { Button } from '../../components/ui';
import { AlertTriangle, X, Loader2 } from 'lucide-react';
import type { SubscriptionResponse } from '../../services/subscription';

export type CancelSubscriptionScope = 'all' | 'academy' | 'club';

interface ConfirmActionModalProps {
  isOpen: boolean;
  title: string;
  intro: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

export function ConfirmActionModal({
  isOpen,
  title,
  intro,
  confirmLabel,
  onClose,
  onConfirm,
  loading = false,
}: ConfirmActionModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-yellow-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="ml-auto text-gray-400 hover:text-gray-600"
            type="button"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{intro}</p>

        <div className="flex space-x-3">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={onConfirm}
            className="flex-1 text-red-600 border-red-600 hover:bg-red-50 hover:text-red-700"
            disabled={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  scope: CancelSubscriptionScope;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading: boolean;
}

const cancelScopeCopy: Record<
  CancelSubscriptionScope,
  { title: string; intro: string; bullets: string[]; confirmLabel: string }
> = {
  all: {
    title: 'Cancel Subscription',
    intro: 'Are you sure you want to cancel your subscription? This action will:',
    bullets: [
      'Cancel your subscription at the end of the current billing period',
      'Stop automatic renewals',
      'Allow you to continue using the service until the period ends',
      'Require you to resubscribe to continue after the period ends',
    ],
    confirmLabel: 'Cancel Subscription',
  },
  academy: {
    title: 'Remove Academy Management',
    intro: 'Are you sure you want to remove academy management from your subscription? This will:',
    bullets: [
      'Take effect immediately with a prorated credit on your next invoice',
      'Remove access to academy lesson management, student portals, and academy-tier features',
      'Keep your club plan and club management features active',
      'Require adding an academy plan again to restore academy features',
    ],
    confirmLabel: 'Remove Academy Management',
  },
  club: {
    title: 'Remove Club Management',
    intro: 'Are you sure you want to remove club management from your subscription? This will:',
    bullets: [
      'Take effect immediately with a prorated credit on your next invoice',
      'Remove access to court hire, memberships, match play, and facility operations',
      'Disable branded club subdomains if you are on Club Pro',
      'Keep your academy plan and academy features active',
    ],
    confirmLabel: 'Remove Club Management',
  },
};

export function CancelSubscriptionModal({
  isOpen,
  scope,
  onClose,
  onConfirm,
  loading,
}: CancelSubscriptionModalProps) {
  if (!isOpen) return null;

  const copy = cancelScopeCopy[scope];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {copy.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="ml-auto text-gray-400 hover:text-gray-600"
            type="button"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {copy.intro}
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
            {copy.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </div>

        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={loading}
          >
            Keep Subscription
          </Button>
          <Button
            variant="outline"
            onClick={onConfirm}
            className="flex-1 text-red-600 border-red-600 hover:bg-red-50 hover:text-red-700"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              copy.confirmLabel
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export type ChangePlanProductLine = 'academy' | 'club';

interface ChangePlanModalProps {
  isOpen: boolean;
  selectedPlan: Plan | null;
  currentSubscription: SubscriptionResponse | null;
  currentClubPlan?: Plan | null;
  productLine: ChangePlanProductLine;
  isCombinedOrg?: boolean;
  onClose: () => void;
  onConfirm: (couponCode?: string) => Promise<void>;
  loading: boolean;
  currencyCode: string;
  currencySymbol: string;
  hasPaymentMethod: boolean;
  onManageBilling: () => void | Promise<void>;
}

export function ChangePlanModal({
  isOpen,
  selectedPlan,
  currentSubscription,
  currentClubPlan = null,
  productLine,
  isCombinedOrg = false,
  onClose,
  onConfirm,
  loading,
  currencyCode,
  currencySymbol,
  hasPaymentMethod,
  onManageBilling,
}: ChangePlanModalProps) {
  const [couponCode, setCouponCode] = React.useState('');
  const [couponError, setCouponError] = React.useState<string | null>(null);
  const [validCoupon, setValidCoupon] = React.useState<{
    name: string;
    percent_off?: number;
    amount_off?: number;
  } | null>(null);
  const [validatingCoupon, setValidatingCoupon] = React.useState(false);
  const [billingRedirectLoading, setBillingRedirectLoading] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setCouponCode('');
      setCouponError(null);
      setValidCoupon(null);
      setBillingRedirectLoading(false);
    }
  }, [isOpen]);

  const handleManageBilling = async () => {
    setBillingRedirectLoading(true);
    try {
      await onManageBilling();
    } catch (error: unknown) {
      setBillingRedirectLoading(false);
      const message = error instanceof Error ? error.message : 'Failed to open billing management';
      toast.error(message);
    }
  };

  const formatMonthlyPrice = (amount: number, discounted = false) => (
    <>
      {currencySymbol}
      {discounted ? amount.toFixed(2) : amount}
      /month
    </>
  );

  if (!isOpen || !selectedPlan) return null;

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    setCouponError(null);
    try {
      const { valid, error, name, percent_off, amount_off } = await import('../../services/subscription').then(m => m.subscriptionService.validateCoupon(couponCode));
      if (valid) {
        setValidCoupon({ name: name!, percent_off, amount_off });
      } else {
        setCouponError(error || 'Invalid coupon');
        setValidCoupon(null);
      }
    } catch {
      setCouponError('Failed to validate coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const selectedPlanMonthlyPrice = (() => {
    const resolved = resolveSubscriptionPlanPrice(selectedPlan, {
      currency: currencyCode,
      interval: 'month',
    });
    return resolved?.amountMajor ?? 0;
  })();

  const currentPlanForLine =
    productLine === 'club' ? currentClubPlan : currentSubscription?.plan ?? null;

  const currentPlanMonthlyPrice = (() => {
    if (!currentPlanForLine) return 0;
    const resolved = resolveSubscriptionPlanPrice(currentPlanForLine, {
      currency: currencyCode,
      interval: 'month',
    });
    return resolved?.amountMajor ?? 0;
  })();

  const discountedPrice = (() => {
    if (!validCoupon) return selectedPlanMonthlyPrice;
    if (validCoupon.percent_off) {
      return selectedPlanMonthlyPrice * (1 - validCoupon.percent_off / 100);
    }
    if (validCoupon.amount_off) {
      return Math.max(0, selectedPlanMonthlyPrice - (validCoupon.amount_off / 100));
    }
    return selectedPlanMonthlyPrice;
  })();

  const needsPaymentMethod = !hasPaymentMethod;
  const isClubLine = productLine === 'club';
  const modalTitle = !currentSubscription
    ? 'Subscribe to Plan'
    : isClubLine
      ? 'Change Club Plan'
      : 'Change Academy Plan';

  const introText = !currentSubscription
    ? `You are about to subscribe to the ${selectedPlan.name} plan.`
    : isClubLine
      ? `You are about to change your club plan to ${selectedPlan.name}.`
      : `You are about to change your academy plan to ${selectedPlan.name}.`;

  const combinedTotalPreview = (() => {
    if (!isCombinedOrg || !currentSubscription?.plan || !currentClubPlan) return null;

    const academyLinePrice =
      productLine === 'academy'
        ? discountedPrice
        : (() => {
            const resolved = resolveSubscriptionPlanPrice(currentSubscription.plan!, {
              currency: currencyCode,
              interval: 'month',
            });
            return resolved?.amountMajor ?? 0;
          })();

    const clubLinePrice =
      productLine === 'club'
        ? discountedPrice
        : (() => {
            const resolved = resolveSubscriptionPlanPrice(currentClubPlan, {
              currency: currencyCode,
              interval: 'month',
            });
            return resolved?.amountMajor ?? 0;
          })();

    const subtotal = calculateCombinedBundleSubtotal(academyLinePrice, clubLinePrice);
    return calculateCombinedBundleTotal(subtotal);
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-yellow-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {modalTitle}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="ml-auto text-gray-400 hover:text-gray-600"
            type="button"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {introText}
          </p>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">New Plan:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {selectedPlan.name}
                {' · '}
                {validCoupon ? (
                  <>
                    <span className="line-through text-gray-500 mr-2">
                      {currencySymbol}
                      {selectedPlanMonthlyPrice}
                    </span>
                    <span className="text-green-600">
                      {currencySymbol}
                      {discountedPrice.toFixed(2)}
                    </span>
                    /month
                  </>
                ) : (
                  formatMonthlyPrice(selectedPlanMonthlyPrice)
                )}
              </span>
            </div>
            {currentSubscription && currentPlanForLine && (
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Current Plan:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {currentPlanForLine.name}
                  {' · '}
                  {formatMonthlyPrice(currentPlanMonthlyPrice)}
                </span>
              </div>
            )}
            {combinedTotalPreview != null ? (
              <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-600">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Est. combined total:
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {currencySymbol}
                    {combinedTotalPreview.toFixed(2)}
                    /month
                    <span className="ml-1 text-xs font-normal text-gray-500">
                      ({COMBINED_BUNDLE_DISCOUNT_PERCENT}% bundle discount)
                    </span>
                  </span>
                </div>
              </div>
            ) : null}
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-3">
              {!currentSubscription
                ? "Your subscription will start immediately."
                : "Changes will take effect immediately. You'll be charged the prorated amount."
              }
            </p>
          </div>

          {!isClubLine ? (
            <div className="mb-4">
              <label htmlFor="coupon" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Promo Code
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="coupon"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Enter code"
                  disabled={!!validCoupon || loading}
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm p-2 border"
                />
                {validCoupon ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setValidCoupon(null);
                      setCouponCode('');
                    }}
                    disabled={loading}
                  >
                    Remove
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleValidateCoupon}
                    disabled={!couponCode.trim() || validatingCoupon || loading}
                  >
                    {validatingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                  </Button>
                )}
              </div>
              {couponError && <p className="mt-1 text-sm text-red-600">{couponError}</p>}
              {validCoupon && (
                <p className="mt-1 text-sm text-green-600">
                  Code applied: {validCoupon.name}
                  {validCoupon.percent_off ? ` (${validCoupon.percent_off}% off)` : ''}
                  {validCoupon.amount_off ? ` (${currencySymbol}${validCoupon.amount_off / 100} off)` : ''}
                </p>
              )}
            </div>
          ) : null}

          {needsPaymentMethod && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Payment method required</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      Please add a payment method before changing your plan.
                    </p>
                  </div>
                  <div className="mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void handleManageBilling()}
                      disabled={billingRedirectLoading || loading}
                      className="text-yellow-800 bg-yellow-100 hover:bg-yellow-200 border-yellow-200"
                    >
                      {billingRedirectLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Redirecting...
                        </>
                      ) : (
                        'Add Payment Method'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>

        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(validCoupon && couponCode ? couponCode : undefined)}
            className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
            disabled={loading || needsPaymentMethod}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              !currentSubscription ? 'Subscribe Now' : 'Confirm Change'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
